/**
 * 組織ツリー表示コンポーネント
 */
import { useState, useRef, useEffect, useMemo, useContext, useCallback, createContext } from "react";
import clsx from "clsx";
import { Button } from "../../atoms";
import { updateOrganization, type OrganizationResponse } from "../../../api/organizations";
import { updateStore, type StoreResponse } from "../../../api/stores";
import { fetchStoreDepartments, type DepartmentResponse } from "../../../api/departments";

export interface OrgTreeNode {
    org: OrganizationResponse;
    children: OrgTreeNode[];
    stores: StoreResponse[];
}

export interface OrganizationTreeViewProps {
    organizations: OrganizationResponse[];
    stores: StoreResponse[];
    className?: string;
    onDataChange?: () => void;
}

// --- ポップオーバー排他制御 Context ---

interface PopoverContextValue {
    activeId: string | null;
    setActiveId: (id: string | null) => void;
}

const PopoverContext = createContext<PopoverContextValue>({
    activeId: null,
    setActiveId: () => {},
});

/** ポップオーバー制御フック */
function usePopover(popoverId: string) {
    const { activeId, setActiveId } = useContext(PopoverContext);
    const isOpen = activeId === popoverId;
    const ref = useRef<HTMLDivElement>(null);

    const toggle = useCallback(() => {
        setActiveId(isOpen ? null : popoverId);
    }, [isOpen, popoverId, setActiveId]);

    const close = useCallback(() => {
        if (isOpen) setActiveId(null);
    }, [isOpen, setActiveId]);

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setActiveId(null);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [isOpen, setActiveId]);

    return { isOpen, toggle, close, ref };
}

// --- ツリー構築 ---

/** フラットな一覧からツリーを構築 */
function buildTree(organizations: OrganizationResponse[], stores: StoreResponse[]): OrgTreeNode[] {
    const nodeMap = new Map<number, OrgTreeNode>();

    for (const org of organizations) {
        nodeMap.set(org.id, { org, children: [], stores: [] });
    }

    // store.organization_id で紐付け
    for (const store of stores) {
        if (store.organization_id !== null) {
            const node = nodeMap.get(store.organization_id);
            if (node) {
                node.stores.push(store);
            }
        }
    }

    // 店舗をID順にソート
    for (const node of nodeMap.values()) {
        node.stores.sort((a, b) => a.id - b.id);
    }

    const roots: OrgTreeNode[] = [];
    for (const node of nodeMap.values()) {
        if (node.org.parent_organization_id === null) {
            roots.push(node);
        } else {
            const parent = nodeMap.get(node.org.parent_organization_id);
            if (parent) {
                parent.children.push(node);
            } else {
                roots.push(node);
            }
        }
    }

    return roots;
}

// --- ポップオーバー ---

type OrgNodeType = "store" | "parent-of-stores" | "other";

interface PopoverAction {
    key: string;
    icon: React.FC<React.SVGProps<SVGSVGElement>>;
    label: string;
    hoverColor: string;
    disabled?: boolean;
}

const ACTIONS_BY_TYPE: Record<OrgNodeType, PopoverAction[]> = {
    store: [
        { key: "org-add", icon: PlusCircleIcon, label: "組織追加", hoverColor: "hover:bg-brand-50 hover:text-brand-500", disabled: true },
        { key: "store-edit", icon: EditIcon, label: "店舗編集", hoverColor: "hover:bg-brand-50 hover:text-brand-500" },
        { key: "store-delete", icon: TrashIcon, label: "店舗削除", hoverColor: "hover:bg-red-50 hover:text-red-500" },
    ],
    "parent-of-stores": [
        { key: "org-add", icon: PlusCircleIcon, label: "組織追加", hoverColor: "hover:bg-brand-50 hover:text-brand-500" },
        { key: "store-add", icon: StorePlusIcon, label: "店舗追加", hoverColor: "hover:bg-brand-50 hover:text-brand-500" },
        { key: "edit", icon: EditIcon, label: "編集", hoverColor: "hover:bg-brand-50 hover:text-brand-500" },
        { key: "delete", icon: TrashIcon, label: "削除", hoverColor: "hover:bg-red-50 hover:text-red-500" },
    ],
    other: [
        { key: "org-add", icon: PlusCircleIcon, label: "組織追加", hoverColor: "hover:bg-brand-50 hover:text-brand-500" },
        { key: "edit", icon: EditIcon, label: "編集", hoverColor: "hover:bg-brand-50 hover:text-brand-500" },
        { key: "delete", icon: TrashIcon, label: "削除", hoverColor: "hover:bg-red-50 hover:text-red-500" },
    ],
};

const NodePopover = ({ nodeType, position = "below", onAction }: {
    nodeType: OrgNodeType;
    position?: "above" | "below" | "right";
    onAction?: Record<string, () => void>;
}) => {
    const actions = ACTIONS_BY_TYPE[nodeType];
    return (
        <div className={clsx(
            "absolute z-50",
            position === "above" && "left-1/2 -translate-x-1/2 bottom-full mb-2",
            position === "below" && "left-1/2 -translate-x-1/2 top-full mt-2",
            position === "right" && "left-full ml-2 top-1/2 -translate-y-1/2"
        )}>
            <div className="flex items-center gap-2.5 px-4 py-2.5 bg-brand-100 rounded-full shadow-lg">
                {actions.map((action) => {
                    const handler = onAction?.[action.key];
                    return (
                        <button
                            key={action.key}
                            type="button"
                            disabled={action.disabled}
                            onClick={(e) => { e.stopPropagation(); handler?.(); }}
                            className={clsx(
                                "p-2 rounded-full shadow-sm transition-all",
                                action.disabled
                                    ? "bg-gray-100 text-gray-300 cursor-not-allowed"
                                    : "bg-white text-gray-500 hover:text-brand-600 hover:shadow-md"
                            )}
                            title={action.label}
                        >
                            <action.icon className="w-5 h-5" />
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

// --- ツリーアクション Context ---

interface TreeActionContextValue {
    onStoreDelete: (store: StoreResponse) => void;
    onOrgDelete: (node: OrgTreeNode) => void;
    onStoreEdit: (store: StoreResponse) => void;
    onOrgEdit: (node: OrgTreeNode) => void;
}

const TreeActionContext = createContext<TreeActionContextValue>({
    onStoreDelete: () => {},
    onOrgDelete: () => {},
    onStoreEdit: () => {},
    onOrgEdit: () => {},
});

// --- 削除モーダル ---

/** 店舗削除 確認モーダル */
const DeleteStoreConfirmModal = ({ store, onCancel, onConfirm }: {
    store: StoreResponse;
    onCancel: () => void;
    onConfirm: () => void;
}) => {
    const [departments, setDepartments] = useState<DepartmentResponse[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStoreDepartments(store.id)
            .then((res) => setDepartments(res.items))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [store.id]);

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
                <p className="text-gray-800 font-bold text-center mb-5">
                    以下の店舗を削除します。<br />よろしいですか？
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="font-bold text-gray-800">{store.name}</div>
                    {loading ? (
                        <div className="text-xs text-gray-400 mt-2">読み込み中...</div>
                    ) : departments.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {departments.map((d) => (
                                <span key={d.id} className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">
                                    {d.name}
                                </span>
                            ))}
                        </div>
                    ) : null}
                </div>
                <div className="flex gap-3 justify-center">
                    <Button variant="outline" size="md" onClick={onCancel} className="px-6">
                        キャンセル
                    </Button>
                    <Button variant="danger" size="md" onClick={onConfirm} className="px-6">
                        削除実行
                    </Button>
                </div>
            </div>
        </div>
    );
};

/** 組織削除 確認モーダル（直下の一覧のみ表示） */
const DeleteOrgConfirmModal = ({ node, onCancel, onConfirm }: {
    node: OrgTreeNode;
    onCancel: () => void;
    onConfirm: () => void;
}) => {
    const directStores = node.stores;
    const directChildren = node.children;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
                <p className="text-gray-800 font-bold text-center mb-5">
                    以下を削除します。<br />よろしいですか？
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                    <div className="font-bold text-gray-800">{node.org.name}</div>
                    {directStores.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {directStores.map((s) => (
                                <span key={s.id} className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">{s.name}</span>
                            ))}
                        </div>
                    )}
                    {directChildren.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {directChildren.map((c) => (
                                <span key={c.org.id} className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">{c.org.name}</span>
                            ))}
                        </div>
                    )}
                </div>
                <div className="flex gap-3 justify-center">
                    <Button variant="outline" size="md" onClick={onCancel} className="px-6">
                        キャンセル
                    </Button>
                    <Button variant="danger" size="md" onClick={onConfirm} className="px-6">
                        削除実行
                    </Button>
                </div>
            </div>
        </div>
    );
};

/** 削除エラー モーダル */
const DeleteErrorModal = ({ message, onClose }: { message: string; onClose: () => void }) => (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
        <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
            <p className="text-gray-800 font-bold text-center mb-6">{message}</p>
            <div className="flex justify-center">
                <Button variant="primary" size="md" onClick={onClose} className="px-8">
                    OK
                </Button>
            </div>
        </div>
    </div>
);

// --- 編集モーダル ---

/** 店舗編集モーダル（名称 + 店舗コード） */
const EditStoreModal = ({ store, onCancel, onSave }: {
    store: StoreResponse;
    onCancel: () => void;
    onSave: (storeId: number, name: string, code: string) => void;
}) => {
    const [name, setName] = useState(store.name);
    const [code, setCode] = useState(store.code ?? "");

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
                <p className="text-gray-800 font-bold text-center mb-5">店舗を編集</p>
                <div className="flex flex-col gap-4 mb-6">
                    <label className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-gray-600">店舗名</span>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-800 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500" />
                    </label>
                    <label className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-gray-600">店舗コード</span>
                        <input type="text" value={code} onChange={(e) => setCode(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-800 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500" />
                    </label>
                </div>
                <div className="flex gap-3 justify-center">
                    <Button variant="outline" size="md" onClick={onCancel} className="px-6">キャンセル</Button>
                    <Button variant="primary" size="md" disabled={!name.trim()} onClick={() => onSave(store.id, name.trim(), code.trim())} className="px-6">保存</Button>
                </div>
            </div>
        </div>
    );
};

/** 組織編集モーダル（名称のみ） */
const EditOrgModal = ({ node, onCancel, onSave }: {
    node: OrgTreeNode;
    onCancel: () => void;
    onSave: (orgId: number, name: string) => void;
}) => {
    const [name, setName] = useState(node.org.name ?? "");

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
                <p className="text-gray-800 font-bold text-center mb-5">組織を編集</p>
                <div className="flex flex-col gap-4 mb-6">
                    <label className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-gray-600">名称</span>
                        <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-300 text-sm text-gray-800 focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500" />
                    </label>
                </div>
                <div className="flex gap-3 justify-center">
                    <Button variant="outline" size="md" onClick={onCancel} className="px-6">キャンセル</Button>
                    <Button variant="primary" size="md" disabled={!name.trim()} onClick={() => onSave(node.org.id, name.trim())} className="px-6">保存</Button>
                </div>
            </div>
        </div>
    );
};

// --- ノードコンポーネント ---

/** 組織ノードボックス（ダブルクリックでポップオーバー） */
const OrgUnitBox = ({ node, nodeType }: { node: OrgTreeNode; nodeType: OrgNodeType }) => {
    const { isOpen, toggle, ref } = usePopover(`org-${node.org.id}`);
    const { onOrgDelete, onOrgEdit } = useContext(TreeActionContext);

    const popoverActions = useMemo(() => ({
        edit: () => onOrgEdit(node),
        delete: () => onOrgDelete(node),
    }), [onOrgEdit, onOrgDelete, node]);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onDoubleClick={toggle}
                className={clsx(
                    "px-10 py-4 rounded-lg border-2 bg-white text-gray-800 font-bold text-base shadow-sm whitespace-nowrap transition-all",
                    isOpen ? "border-brand-600 shadow-md" : "border-brand-500 hover:shadow-md"
                )}
            >
                {node.org.name}
            </button>
            {isOpen && (
                <NodePopover
                    nodeType={nodeType}
                    position={node.org.parent_organization_id === null ? "right" : "above"}
                    onAction={popoverActions}
                />
            )}
        </div>
    );
};

/** 店舗カード（ダブルクリックでポップオーバー） */
const StoreCard = ({ store }: { store: StoreResponse }) => {
    const { isOpen, toggle, ref } = usePopover(`store-${store.id}`);
    const { onStoreDelete, onStoreEdit } = useContext(TreeActionContext);

    const popoverActions = useMemo(() => ({
        "store-edit": () => onStoreEdit(store),
        "store-delete": () => onStoreDelete(store),
    }), [onStoreEdit, onStoreDelete, store]);

    return (
        <div ref={ref} className="relative">
            <button
                type="button"
                onDoubleClick={toggle}
                className={clsx(
                    "w-full px-4 py-2.5 rounded-lg border bg-white text-left transition-all",
                    isOpen ? "border-brand-500 shadow-md" : "border-gray-200 hover:border-brand-300 hover:shadow-md"
                )}
            >
                <div className="text-sm font-bold text-gray-800">{store.name}</div>
                {store.code && <div className="text-xs text-gray-400 mt-0.5">{store.code}</div>}
            </button>
            {isOpen && <NodePopover nodeType="store" onAction={popoverActions} />}
        </div>
    );
};

/** 店舗内包カラム（親orgヘッダー + 店舗カード一覧） */
const StoreColumn = ({ node }: { node: OrgTreeNode }) => {
    const { isOpen, toggle, ref } = usePopover(`column-${node.org.id}`);
    const { onOrgDelete, onOrgEdit } = useContext(TreeActionContext);

    const popoverActions = useMemo(() => ({
        edit: () => onOrgEdit(node),
        delete: () => onOrgDelete(node),
    }), [onOrgEdit, onOrgDelete, node]);

    return (
        <div ref={ref} className="relative flex flex-col rounded-lg border-2 border-brand-500 bg-white shadow-sm min-w-[220px] max-w-[260px]">
            <button
                type="button"
                onDoubleClick={toggle}
                className="px-6 py-3 text-center font-bold text-base text-gray-800 border-b border-gray-100 rounded-t-lg hover:bg-gray-50 transition-colors"
            >
                {node.org.name}
            </button>
            <div className="p-3 flex flex-col gap-2.5">
                {node.stores.map((store) => (
                    <StoreCard key={store.id} store={store} />
                ))}
            </div>
            {isOpen && <NodePopover nodeType="parent-of-stores" position="above" onAction={popoverActions} />}
        </div>
    );
};

/** ツリーブランチ（再帰） */
const TreeBranch = ({ node }: { node: OrgTreeNode }) => {
    const hasChildren = node.children.length > 0;
    const hasStores = node.stores.length > 0;

    // 店舗を持つリーフノード → StoreColumn で描画
    if (hasStores && !hasChildren) {
        return <StoreColumn node={node} />;
    }

    return (
        <div className="flex flex-col items-center">
            <OrgUnitBox node={node} nodeType="other" />

            {/* 子ノードを再帰表示 */}
            {hasChildren && (
                <>
                    <div className="w-0.5 h-8 bg-gray-300" />
                    <div className="flex items-start">
                        {node.children.map((child, index) => {
                            const isFirst = index === 0;
                            const isLast = index === node.children.length - 1;
                            const isOnly = node.children.length === 1;
                            return (
                                <div key={child.org.id} className="flex flex-col items-center relative px-5">
                                    {!isOnly && (
                                        <div
                                            className={clsx(
                                                "absolute top-0 h-0.5 bg-gray-300",
                                                isFirst && "left-1/2 right-0",
                                                isLast && "left-0 right-1/2",
                                                !isFirst && !isLast && "left-0 right-0"
                                            )}
                                        />
                                    )}
                                    <div className="w-0.5 h-6 bg-gray-300" />
                                    <TreeBranch node={child} />
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

const OrganizationTreeView = ({ organizations, stores, className, onDataChange }: OrganizationTreeViewProps) => {
    const tree = useMemo(() => buildTree(organizations, stores), [organizations, stores]);
    const [activeId, setActiveId] = useState<string | null>(null);
    const popoverContext = useMemo(() => ({ activeId, setActiveId }), [activeId]);

    // 店舗削除フロー
    const [storeDeleteTarget, setStoreDeleteTarget] = useState<StoreResponse | null>(null);
    // 組織削除フロー
    const [orgDeleteTarget, setOrgDeleteTarget] = useState<OrgTreeNode | null>(null);
    // エラーモーダル
    const [deleteErrorMessage, setDeleteErrorMessage] = useState<string | null>(null);
    // 店舗編集フロー
    const [storeEditTarget, setStoreEditTarget] = useState<StoreResponse | null>(null);
    // 組織編集フロー
    const [orgEditTarget, setOrgEditTarget] = useState<OrgTreeNode | null>(null);

    const handleStoreDelete = useCallback((store: StoreResponse) => {
        setActiveId(null);
        setStoreDeleteTarget(store);
    }, []);

    const handleOrgDelete = useCallback((node: OrgTreeNode) => {
        setActiveId(null);
        setOrgDeleteTarget(node);
    }, []);

    const handleDeleteCancel = useCallback(() => {
        setStoreDeleteTarget(null);
        setOrgDeleteTarget(null);
    }, []);

    const handleStoreDeleteConfirm = useCallback(() => {
        setStoreDeleteTarget(null);
        setDeleteErrorMessage("店舗に従業員が存在するため、\n削除できませんでした。");
    }, []);

    const handleOrgDeleteConfirm = useCallback(() => {
        setOrgDeleteTarget(null);
        setDeleteErrorMessage("従業員が存在する店舗があるため、\n削除できませんでした。");
    }, []);

    const handleErrorClose = useCallback(() => {
        setDeleteErrorMessage(null);
    }, []);

    // 編集フロー
    const handleStoreEdit = useCallback((store: StoreResponse) => {
        setActiveId(null);
        setStoreEditTarget(store);
    }, []);

    const handleOrgEdit = useCallback((node: OrgTreeNode) => {
        setActiveId(null);
        setOrgEditTarget(node);
    }, []);

    const handleEditCancel = useCallback(() => {
        setStoreEditTarget(null);
        setOrgEditTarget(null);
    }, []);

    const handleStoreEditSave = useCallback(async (storeId: number, name: string, code: string) => {
        try {
            await updateStore(storeId, { name, code: code || null });
            setStoreEditTarget(null);
            onDataChange?.();
        } catch (err) {
            console.error("店舗の更新に失敗しました", err);
        }
    }, [onDataChange]);

    const handleOrgEditSave = useCallback(async (orgId: number, name: string) => {
        try {
            await updateOrganization(orgId, { name });
            setOrgEditTarget(null);
            onDataChange?.();
        } catch (err) {
            console.error("組織の更新に失敗しました", err);
        }
    }, [onDataChange]);

    const actionContext = useMemo(() => ({
        onStoreDelete: handleStoreDelete,
        onOrgDelete: handleOrgDelete,
        onStoreEdit: handleStoreEdit,
        onOrgEdit: handleOrgEdit,
    }), [handleStoreDelete, handleOrgDelete, handleStoreEdit, handleOrgEdit]);

    return (
        <PopoverContext.Provider value={popoverContext}>
            <TreeActionContext.Provider value={actionContext}>
                <div className={clsx("flex flex-col items-center pt-10 pb-20 w-fit min-w-full", className)}>
                    {tree.map((root) => (
                        <TreeBranch key={root.org.id} node={root} />
                    ))}
                </div>
                {storeDeleteTarget && (
                    <DeleteStoreConfirmModal store={storeDeleteTarget} onCancel={handleDeleteCancel} onConfirm={handleStoreDeleteConfirm} />
                )}
                {orgDeleteTarget && (
                    <DeleteOrgConfirmModal node={orgDeleteTarget} onCancel={handleDeleteCancel} onConfirm={handleOrgDeleteConfirm} />
                )}
                {deleteErrorMessage && (
                    <DeleteErrorModal message={deleteErrorMessage} onClose={handleErrorClose} />
                )}
                {storeEditTarget && (
                    <EditStoreModal store={storeEditTarget} onCancel={handleEditCancel} onSave={handleStoreEditSave} />
                )}
                {orgEditTarget && (
                    <EditOrgModal node={orgEditTarget} onCancel={handleEditCancel} onSave={handleOrgEditSave} />
                )}
            </TreeActionContext.Provider>
        </PopoverContext.Provider>
    );
};

export default OrganizationTreeView;

// --- アイコン ---

function PlusCircleIcon(p: React.SVGProps<SVGSVGElement>) {
    return (
        <svg {...p} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
        </svg>
    );
}

function EditIcon(p: React.SVGProps<SVGSVGElement>) {
    return (
        <svg {...p} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
        </svg>
    );
}

function TrashIcon(p: React.SVGProps<SVGSVGElement>) {
    return (
        <svg {...p} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
        </svg>
    );
}

function StorePlusIcon(p: React.SVGProps<SVGSVGElement>) {
    return (
        <svg {...p} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v-3a1.5 1.5 0 0 1 1.5-1.5h0a1.5 1.5 0 0 1 1.5 1.5v3" />
            <circle cx="20" cy="5" r="4" fill="white" stroke="none" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 3v4m2-2h-4" />
        </svg>
    );
}
